import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Lock } from 'lucide-react';
import { getBranch } from '../data/catalog';
import { tutorsForBranch } from '../data/tutors';
import { getPreferredTutorId, openTutorCalendly } from '../services/booking';
import { verifyCheckout, CheckoutVerification } from '../services/api';
import TutorCard from '../components/TutorCard';
import usePageMeta from '../hooks/usePageMeta';
import '../styles/Booking.css';

type BookingState =
  | { status: 'locked' }
  | { status: 'verifying' }
  | { status: 'error' }
  | { status: 'paid'; verification: CheckoutVerification };

const BookingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState<BookingState>(
    sessionId ? { status: 'verifying' } : { status: 'locked' }
  );

  usePageMeta({
    title: 'Book Your Tutor | Bulge Bracket',
    description: 'Choose your exact tutor and book your first session.',
    path: '/booking'
  });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'locked' });
      return;
    }
    let cancelled = false;
    setState({ status: 'verifying' });
    verifyCheckout(sessionId)
      .then(verification => {
        if (cancelled) return;
        setState(verification.paid ? { status: 'paid', verification } : { status: 'error' });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (state.status === 'locked' || state.status === 'error') {
    return (
      <section className="section booking-gate">
        <div className="container booking-gate-inner">
          <span className="gate-icon" aria-hidden="true">
            <Lock size={22} />
          </span>
          <h1 className="booking-title">
            {state.status === 'locked'
              ? 'Tutor booking unlocks after checkout.'
              : 'We couldn’t confirm that payment.'}
          </h1>
          <p className="booking-lede">
            {state.status === 'locked'
              ? 'Browse tutors’ calendars on your track page and find a time you like, then pay. This page opens the moment checkout completes so you can lock the slot in.'
              : 'If you were charged, your booking link arrived by email. If not, write to hello@bulgebracket.co and we’ll sort it out.'}
          </p>
          <div className="booking-actions">
            <Link className="btn btn-primary" to="/#tracks">
              Choose your track
            </Link>
            <a className="btn btn-ghost" href="mailto:hello@bulgebracket.co">
              Email support
            </a>
          </div>
        </div>
      </section>
    );
  }

  if (state.status === 'verifying') {
    return (
      <section className="section booking-gate">
        <div className="container booking-gate-inner">
          <h1 className="booking-title">Confirming your payment…</h1>
          <p className="booking-lede">Stripe is confirming the checkout.</p>
        </div>
      </section>
    );
  }

  const { verification } = state;
  const branch = getBranch(verification.branch);
  const tutors = branch ? tutorsForBranch(branch.id) : [];
  const preferred = tutors.find(tutor => tutor.id === getPreferredTutorId());

  return (
    <>
      <section className="booking-confirm">
        <div className="container">
          <p className="confirm-chip">
            <CheckCircle2 size={16} aria-hidden="true" />
            Payment confirmed
          </p>
          <h1 className="booking-title">
            You&apos;re in. Now lock in your time.
          </h1>
          <p className="booking-lede">
            <strong>{verification.serviceName}</strong>
            {branch ? ` for ${branch.name}.` : '.'}{' '}
            {verification.email
              ? `A receipt is on its way to ${verification.email}.`
              : 'Your Stripe receipt arrives by email.'}{' '}
            Confirm the slot you picked earlier, or choose any tutor below.
          </p>
          {preferred && (
            <button
              className="btn btn-primary booking-preferred"
              onClick={() => openTutorCalendly(preferred)}
            >
              Lock in your time with {preferred.initials} →
            </button>
          )}
        </div>
      </section>

      <section className="section booking-roster">
        <div className="container">
          <div className="section-head" data-reveal>
            <p className="section-eyebrow">{branch ? branch.name : 'Your'} tutors</p>
            <h2 className="section-title">Choose exactly who prepares you.</h2>
            <p className="section-intro">
              Every tutor below is a current employee in the seats you&apos;re
              targeting. Pick one and the calendar opens right here.
            </p>
          </div>
          <div className="tutor-grid">
            {tutors.map((tutor, index) => (
              <TutorCard key={tutor.id} tutor={tutor} bookable revealDelay={index * 0.08} />
            ))}
          </div>
          <p className="booking-help" data-reveal>
            Not ready to pick a time? We contact every new student within 12
            hours. To split sessions across tutors, or anything else, email{' '}
            <a href="mailto:hello@bulgebracket.co">hello@bulgebracket.co</a>.
            Purchases cover any tutor in your track.
          </p>
        </div>
      </section>
    </>
  );
};

export default BookingPage;
